<template>
  <div>
    <div class="d-flex justify-content-between">
      <h1 class="page-header">{{ $t('accountReceivable.pageTitle') }}</h1>
      <ts-breadcrumb
        :items="[
          { text: $t('home'), href: '/' },
          {
            text: $t('accountReceivable.pageTitle'),
            active: true,
          },
        ]"
      />
    </div>
    <div class="row">
      <div class="col-md-3">
        <ts-card>
          <ts-card-wrapper>
            <ts-loading-banner :loading="loading">
              <form>
                <fieldset>
                  <legend>{{ $t('accountReceivable.criteria') }}</legend>
                  <div class="row form-group">
                    <div class="col-md-12">
                      <label class="required">{{
                        $t('accountReceivable.date')
                      }}</label>
                      <a-range-picker
                        style="width: 100%"
                        v-model="model.date_range"
                        format="DD-MM-YYYY"
                        valueFormat="DD-MM-YYYY"
                      />
                      <div class="validate" v-if="errors.has('date_range')">
                        {{ errors.first('date_range') }}
                      </div>
                    </div>
                  </div>
                  <div class="row form-group">
                    <div class="col-md-12">
                      <label class="required">{{
                        $t('incomeBySale.branch')
                      }}</label>
                      <a-select
                        style="width: 100%"
                        :filterOption="
                          (input, option) =>
                            option.componentOptions.children[0].text
                              .toLowerCase()
                              .indexOf(input.toLowerCase()) >= 0
                        "
                        v-model.number="model.branch_id"
                        :options="branchList"
                      ></a-select>
                      <div class="validate" v-if="errors.has('branch_id')">
                        {{ errors.first('branch_id') }}
                      </div>
                    </div>
                  </div>
                  <div class="row form-group">
                    <div class="col-md-12">
                      <label>{{ $t('accountReceivable.saleName') }}</label>
                      <a-select
                        style="width: 100%"
                        :filterOption="
                          (input, option) =>
                            option.componentOptions.children[0].text
                              .toLowerCase()
                              .indexOf(input.toLowerCase()) >= 0
                        "
                        v-model="model.sale_id"
                        :options="sales"
                        mode="multiple"
                        :maxTagCount="1"
                        :placeholder="$t('all')"
                      ></a-select>
                      <div class="validate" v-if="errors.has('sale_id')">
                        {{ errors.first('sale_id') }}
                      </div>
                    </div>
                  </div>
                  <div class="row form-group">
                    <div class="col-md-12">
                      <label>{{ $t('accountReceivable.customerName') }}</label>
                      <a-select
                        style="width: 100%"
                        :filterOption="
                          (input, option) =>
                            option.componentOptions.children[0].text
                              .toLowerCase()
                              .indexOf(input.toLowerCase()) >= 0
                        "
                        v-model="model.customer_id"
                        @search="handleSearchCustomer"
                        :options="customerLists"
                        mode="multiple"
                        :maxTagCount="1"
                        :placeholder="$t('all')"
                        :loading="customer_loading"
                      ></a-select>
                      <div class="validate" v-if="errors.has('customer_id')">
                        {{ errors.first('customer_id') }}
                      </div>
                    </div>
                  </div>

                  <div class="row form-group">
                    <a-radio-group
                      v-model="model.report_type"
                      class="col-md-12"
                    >
                      <a-radio-button value="1" class="col-md-6"
                        >Details</a-radio-button
                      >
                      <a-radio-button value="2" class="col-md-6"
                        >Summary</a-radio-button
                      >
                    </a-radio-group>
                  </div>
                  <div class="row">
                    <div class="col-md-6 text-right">
                      <ts-button
                        class="btn-block"
                        outline
                        color="success"
                        @click.prevent="exportExcel"
                        :waiting="exporting"
                        ><i class="fas fa-file-excel"></i>
                        {{ $t('excel') }}</ts-button
                      >
                    </div>
                    <div class="col-md-6 text-right">
                      <ts-button
                        class="btn-block"
                        color="primary"
                        @click.prevent="preview"
                        :waiting="waiting"
                        ><i class="fas fa-file-pdf text-red"></i>
                        {{ $t('preview') }}</ts-button
                      >
                    </div>
                  </div>
                </fieldset>
              </form>
            </ts-loading-banner>
          </ts-card-wrapper>
        </ts-card>
      </div>
      <div class="col-md-9">
        <ts-card>
          <ts-card-wrapper>
            <form>
              <fieldset>
                <legend>{{ $t('accountReceivable.preview') }}</legend>
                <ts-preview-pdf v-model="waiting" :src="src" />
              </fieldset>
            </form>
          </ts-card-wrapper>
        </ts-card>
      </div>
    </div>
  </div>
</template>
<script>
import { mapActions } from 'vuex'
import moment from 'moment'
import { Errors } from 'form-backend-validation'
import * as FileDownload from 'downloadjs'
import { trim, debounce } from 'lodash'

export default {
  name: 'accountReceivable',
  data() {
    return {
      loading: false,
      waiting: false,
      exporting: false,
      customer_loading: false,
      src: null,
      errors: new Errors(),
      sales: [],
      customers: [],
      branchList: [],
      model: {
        sale_id: [],
        customer_id: [],
        branch_id: undefined,
        report_type: '1',
        date_range: [
          moment().format('DD-MM-YYYY'),
          moment().format('DD-MM-YYYY'),
        ],
      },
    }
  },
  computed: {
    customerLists() {
      if (this.model.sale_id.length > 0) {
        return this.customers
          .filter((c) => _.includes(this.model.sale_id, c.sale_id))
          .map((el) => ({
            value: el.customer_id,
            label: el.customer_name,
          }))
      }
      return this.customers.map((el) => ({
        value: el.customer_id,
        label: el.customer_name,
      }))
    },
  },
  created() {
    this.fetchData()
  },
  methods: {
    ...mapActions('report/finance', ['accountReceivable']),
    handleSearchCustomer: debounce(async function (value) {
      if (!value) {
        return false
      } else {
        this.customer_loading = true
        await this.$store
          .dispatch('report/resource/getCustomer', {
            searchCustomerName: value,
          })
          .then((response) => {
            this.customers = response.data
          })
          .catch((error) => {
            this.$toasted.error(error.message)
          })
          .finally(() => {
            this.customer_loading = false
          })
      }
    }, 500),
    async fetchData() {
      this.loading = true
      await this.$store
        .dispatch('security/user/getBranchAssigned')
        .then((response) => {
          this.branchList = response.data.map((el) => ({
            value: el.branch_id,
            label: el.branch_name_en,
          }))
          this.model.branch_id =
            this.$store.state.authUser.user.default_branch.branch_id
        })
      await this.$store
        .dispatch('report/resource/getEmployee')
        .then((response) => {
          this.sales = response.data.map((el) => ({
            value: el.employee_id,
            label: el.employee_name_en,
          }))
        })
      await this.$store
        .dispatch('report/resource/getCustomer')
        .then((response) => {
          this.customers = response.data
        })
        .catch((error) => {
          this.$toasted.error(error.message)
        })
        .finally(() => {
          this.loading = false
        })
    },
    preview() {
      this.waiting = true
      this.src = ''
      this.errors = new Errors()
      this.accountReceivable(this.model)
        .then((response) => {
          this.src = response.url
        })
        .catch((error) => {
          this.$toasted.error(error.message)
          this.errors = new Errors(error.errors)
        })
        .finally(() => {
          this.waiting = false
        })
    },
    exportExcel() {
      this.exporting = true
      this.errors = new Errors()
      this.$store
        .dispatch('report/finance/accountReceivableExcel', this.model)
        .then((response) => {
          let filename = trim(
            response.headers['content-disposition']
              .substring(
                response.headers['content-disposition'].indexOf('filename')
              )
              .replace('filename=', ''),
            '"'
          )
          FileDownload(response.data, filename)
        })
        .catch((err) => {
          let error = JSON.parse(
            String.fromCharCode.apply(null, new Uint8Array(err))
          )
          this.$toasted.error(error.message)
          this.errors = new Errors(error.errors)
        })
        .finally(() => (this.exporting = false))
    },
  },
}
</script>
